import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  LoadingOverlay,
  Switch,
  TextInput,
} from "@mantine/core";
import {
  AlertCircle,
  ChevronLeft,
  Eye,
  EyeOff,
} from "tabler-icons-react";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { useAuth } from "../contexts/Auth";
import { authService } from "../services/authService";

function AuthAlert({ title, children }) {
  return (
    <Alert>
      <AlertCircle size={16} />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

function PageBackdrop() {
  return <div className="auth-page-backdrop" aria-hidden />;
}

function BrandMark() {
  return (
    <svg
      viewBox="0 0 40 40"
      aria-hidden
      className="auth-centered-mark"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <circle
        cx="20"
        cy="20"
        r="17"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1"
        strokeDasharray="2 3"
      />
      <rect x="11" y="11" width="18" height="18" rx="3" fill="currentColor" />
      <path
        d="M16 22.5c.6.7 1.7 1.2 2.9 1.2 1.5 0 2.6-.7 2.6-1.8 0-1-.7-1.5-2.2-1.8l-.9-.2c-.9-.2-1.3-.5-1.3-1 0-.6.6-1 1.4-1 .9 0 1.5.4 1.7 1l1.4-.5c-.3-1.1-1.4-1.8-3-1.8-1.6 0-2.7.8-2.7 2 0 1 .7 1.6 2.1 1.9l.9.2c.9.2 1.4.5 1.4 1.1 0 .6-.6 1-1.5 1-1 0-1.7-.4-2-1.1l-1.5.6Z"
        fill="var(--bg)"
      />
    </svg>
  );
}

function TwoFactorMark() {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden
      className="auth-centered-mark"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <rect width="64" height="64" rx="14" fill="url(#tfa-bg)" />
      <g transform="translate(32 32) rotate(-25)">
        <path
          d="M -23 0 A 23 8 0 0 1 23 0"
          stroke="currentColor"
          strokeOpacity="0.45"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
      </g>
      <circle cx="32" cy="32" r="13" fill="url(#tfa-sphere)" />
      <g transform="translate(32 32) rotate(-25)">
        <path
          d="M 23 0 A 23 8 0 0 1 -23 0"
          stroke="currentColor"
          strokeOpacity="0.85"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
      </g>
      <defs>
        <radialGradient id="tfa-bg" cx="50%" cy="15%" r="95%">
          <stop offset="0%" stopColor="#1d1d2c" />
          <stop offset="100%" stopColor="#0a0a0f" />
        </radialGradient>
        <linearGradient id="tfa-sphere" x1="20%" y1="20%" x2="80%" y2="80%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#b9b9d2" />
          <stop offset="100%" stopColor="#5a5a78" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function CodeFields({ code, setCode, remember, setRemember }) {
  const updateDigit = (index, value) => {
    const digits = value.replace(/\D/g, "");
    const next = code.padEnd(6, " ").split("");
    next[index] = digits.slice(-1) || " ";
    setCode(next.join("").replace(/\s/g, "").slice(0, 6));
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    setCode(pasted);
  };

  return (
    <>
      <div className="otp-box-row" onPaste={handlePaste}>
        {Array.from({ length: 6 }).map((_, index) => (
          <React.Fragment key={index}>
            {index === 3 ? <span className="otp-separator" /> : null}
            <input
              aria-label={`Digit ${index + 1}`}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              autoComplete={index === 0 ? "one-time-code" : "off"}
              value={code[index] || ""}
              onChange={(event) => {
                updateDigit(index, event.currentTarget.value);
                if (event.currentTarget.value && index < 5) {
                  event.currentTarget.parentElement
                    ?.querySelectorAll("input")
                    ?.[index + 1]?.focus();
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && !code[index] && index > 0) {
                  event.currentTarget.parentElement
                    ?.querySelectorAll("input")
                    ?.[index - 1]?.focus();
                }
              }}
            />
          </React.Fragment>
        ))}
      </div>

    </>
  );
}

function RecoveryFields({ recoveryCode, setRecoveryCode }) {
  return (
    <TextInput
      required
      autoFocus
      id="recovery-code"
      type="text"
      autoComplete="one-time-code"
      placeholder="xxxxx-xxxxx"
      value={recoveryCode}
      onChange={(event) => setRecoveryCode(event.currentTarget.value)}
      className="auth-input recovery-input"
    />
  );
}

export function Login() {
  const { signIn, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpMode, setOtpMode] = useState("code");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [isProduction, setIsProduction] = useState(
    () => authService.getEnvironment() === "production"
  );

  const history = useHistory();

  useEffect(() => {
    if (user) history.push("/");
  }, [user, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setLoading(true);
    setError("");

    try {
      const credentials = requiresOTP
        ? {
            email,
            password,
            otpCode: otpMode === "code" ? otpCode : recoveryCode,
            rememberDevice,
          }
        : { email, password };

      const result = await signIn(credentials);

      if (result.error) {
        setError(result.error);
        setLoading(false);
      } else if (result.requiresOTP) {
        setRequiresOTP(true);
        setOtpMessage(result.message || "Please enter your OTP code");
        setLoading(false);
      } else if (result.user) {
        setLoading(false);
        history.push("/");
      } else {
        setError("Unexpected response from server");
        setLoading(false);
      }
    } catch (err) {
      setError("An unexpected error occurred");
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequiresOTP(false);
    setOtpCode("");
    setOtpMode("code");
    setRecoveryCode("");
    setRememberDevice(false);
    setError("");
    setOtpMessage("");
  };

  const handleEnvironmentChange = (event) => {
    const enabled = event.currentTarget.checked;
    setIsProduction(enabled);
    authService.setEnvironment(enabled ? "production" : "staging");
  };

  return (
    <main className="auth-centered-shell">
      <PageBackdrop />
      <div className="auth-centered-card">
        <LoadingOverlay visible={loading} overlayBlur={2} />

        {!requiresOTP ? (
          <>
            <header className="auth-centered-header">
              <BrandMark />
              <div>
                <h1>Welcome back</h1>
                <p>Sign in to your Bugsjoy dashboard</p>
              </div>
            </header>

            <section className="auth-centered-panel">
              <form onSubmit={handleSubmit} className="auth-centered-form">
                <div className="auth-env-row auth-env-row--centered">
                  <div>
                    <span>Environment</span>
                    <strong>{isProduction ? "Production" : "Staging"}</strong>
                  </div>
                  <Switch
                    checked={isProduction}
                    onChange={handleEnvironmentChange}
                    size="md"
                  />
                </div>

                {error ? (
                  <AuthAlert variant="error" title="Sign in failed">
                    {error}
                  </AuthAlert>
                ) : null}

                <TextInput
                  required
                  label="Email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                  type="email"
                  autoComplete="email"
                  className="auth-input"
                />

                <TextInput
                  required
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => setPassword(event.currentTarget.value)}
                  type={revealPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className="auth-input"
                  rightSectionWidth={42}
                  rightSection={
                    <button
                      type="button"
                      className="password-reveal"
                      onClick={() => setRevealPassword((value) => !value)}
                      aria-label={revealPassword ? "Hide password" : "Show password"}
                    >
                      {revealPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  }
                />

                <Button type="submit" size="lg" className="auth-submit">
                  Sign in
                </Button>
              </form>
            </section>
          </>
        ) : (
          <>
            <header className="auth-centered-header auth-otp-header">
              <button
                type="button"
                className="auth-cancel"
                onClick={handleBackToLogin}
              >
                <ChevronLeft size={14} />
                Cancel
              </button>
              <TwoFactorMark />
              <div>
                <span className="auth-step-label">Step 2 of 2</span>
                <h1>Two-factor authentication</h1>
                <p>
                  {otpMode === "code"
                    ? otpMessage ||
                      "Open your authenticator app and enter the 6-digit code."
                    : "Enter one of your one-time recovery codes."}
                </p>
              </div>
            </header>

            <section className="auth-centered-panel">
              <form className="auth-centered-form" onSubmit={handleSubmit}>
                {error ? (
                  <AuthAlert variant="error" title="Verification failed">
                    {error}
                  </AuthAlert>
                ) : null}

                {otpMode === "code" ? (
                  <CodeFields
                    code={otpCode}
                    setCode={setOtpCode}
                    remember={rememberDevice}
                    setRemember={setRememberDevice}
                  />
                ) : (
                  <RecoveryFields
                    recoveryCode={recoveryCode}
                    setRecoveryCode={setRecoveryCode}
                  />
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="auth-submit"
                  disabled={
                    otpMode === "code"
                      ? otpCode.length !== 6
                      : recoveryCode.length < 8
                  }
                >
                  Verify
                </Button>

              </form>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
